import _ from 'lodash';
import { LOCATION_TYPES } from '@/constants/locationTypes';

const defaultOrders = {
    active: [],
    pending: [],
    payable: [],
    complete: [],
};

const searchableProps = [
    'order_number',
    'po_number',
    'business',
    'dropoff_name',
    'pickup_city',
    'dropoff_city',
    'pickup_date',
    'pickup_zip',
    'dropoff_zip',
    'dropoff_location',
    'dropoff_location_info',
    'dropoff_location_type',
    'delivery_date',
    'del_time',
    'pu_date',
    'price',
    'order_type',
    'dropoff_street',
];

// Item attributes so an order can be found by what's on it, e.g. "monogram fridge".
const itemSearchableProps = ['item_type', 'item_type_details', 'description', 'name', 'sku', 'category'];

// Both legs, so a phone search hits whichever number the user knows.
const phoneProps = ['dropoff_phone', 'pickup_phone'];

// Service type is stored as a key (e.g. "whiteGlove"); include the human label
// so "white glove" matches too.
const serviceTypeProps = [
    'dropoff_location_type',
    'dropoff_location_info',
    'pickup_location_type',
    'pickup_location_info',
];

const lower = (val) => (val ?? '').toString().toLowerCase();
const digitsOnly = (val) => (val ?? '').toString().replace(/\D/g, '');

const getItemValues = (order) => {
    // itemsByOrderId is the raw relationship; items is the @client-resolved list.
    // Search both so results are stable regardless of which is populated.
    const itemArrays = [order?.itemsByOrderId, order?.items].filter(Array.isArray);
    const values = [];
    for (const items of itemArrays) {
        for (const item of items) {
            for (const prop of itemSearchableProps) {
                if (item?.[prop]) values.push(lower(item[prop]));
            }
        }
    }
    return values;
};

const getServiceTypeValues = (order) =>
    serviceTypeProps
        .map((prop) => order?.[prop])
        .filter(Boolean)
        .flatMap((key) => [key, LOCATION_TYPES[key]])
        .filter(Boolean)
        .map(lower);

const getPhoneValues = (order) =>
    phoneProps.flatMap((prop) => {
        const raw = order?.[prop];
        return raw ? [lower(raw), digitsOnly(raw)] : [];
    });

const getSearchableValues = (order) => [
    ...Object.values(_.pick(order, searchableProps)).map((val) => {
        if (val?.constructor === String) {
            return val.toLowerCase();
        }

        return val?.toString() || '';
    }),
    ...getServiceTypeValues(order),
    ...getItemValues(order),
    ...getPhoneValues(order),
];

export const includesSearchProp = (order, searchString) => {
    // Match every whitespace-separated token independently so multi-word queries
    // like "monogram fridge" hit even when the words live in different fields.
    const tokens = (searchString || '').trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
        return true;
    }

    const haystack = getSearchableValues(order).join(' ');
    const phoneDigits = phoneProps.map((prop) => digitsOnly(order?.[prop])).filter(Boolean);

    return tokens.every((token) => {
        if (haystack.includes(token)) {
            return true;
        }

        // Phone fallback: compare digits only so "(704) 555-1234", "704-555-1234"
        // and "7045551234" all match the same stored number.
        const tokenDigits = digitsOnly(token);
        return tokenDigits.length >= 3 && phoneDigits.some((digits) => digits.includes(tokenDigits));
    });
};

const initializeSearchedOrders = (allOrders) => {
    const searchedOrders = {};

    const orderStatuses = Object.keys(allOrders);

    for (const orderStatus of orderStatuses) {
        searchedOrders[orderStatus] = [];
    }

    return searchedOrders;
};

const searchOrders = (searchString = '', allOrders = defaultOrders) => {
    const lCaseSearchString = searchString.toLowerCase();

    const searchedOrders = initializeSearchedOrders(allOrders);

    Object.keys(allOrders).forEach((orderStatus) => {
        for (const order of allOrders[orderStatus]) {
            const included = includesSearchProp(order, lCaseSearchString);

            if (included) {
                searchedOrders[orderStatus].push(order);
            }
        }
    });

    return searchedOrders;
};

export default searchOrders;
